/**
 * Authentication API Client for Secret Book Price Frontend
 *
 * This module provides a type-safe client for interacting with the SBP auth API.
 * It handles Bronze (device-based) and Silver/Gold (magic link) authentication.
 *
 * Features:
 * - Bronze tier: Device-based registration and login
 * - Silver/Gold tier: Magic link email authentication
 * - Token storage in localStorage
 * - Session management
 *
 * @module web/lib/auth
 */

import * as Sentry from "@sentry/react";
import { config } from "../config";

// =============================================================================
// Types & Interfaces
// =============================================================================

/**
 * User tier type
 */
export type AuthTier = "bronze" | "silver" | "gold";

/**
 * Auth error response
 */
export interface AuthError {
  code: string;
  message: string;
}

/**
 * Bronze registration request
 */
export interface BronzeRegisterRequest {
  device_fingerprint?: string;
}

/**
 * Bronze registration response
 */
export interface BronzeRegisterResponse {
  username: string;
  device_token: string;
  tier: string;
  daily_scan_limit: number;
  message: string;
}

/**
 * Bronze login response
 */
export interface BronzeLoginResponse {
  username: string;
  token: string;
  tier: string;
  scan_limit: number;
}

/**
 * Magic link send request
 */
export interface MagicLinkSendRequest {
  email: string;
  device_token?: string; // For Bronze → Silver upgrade
}

/**
 * Magic link send response
 */
export interface MagicLinkSendResponse {
  message: string;
  email: string;
  will_upgrade: boolean;
  expires_in_minutes: number;
}

/**
 * Authenticated user response
 */
export interface AuthenticatedUser {
  id: number;
  email: string;
  username: string;
  tier: string;
  daily_limit: number;
  scans_used_today: number;
  scans_remaining: number;
  is_unlimited: boolean;
  email_verified: boolean;
  upgraded_from_bronze: boolean;
  next_survey_due_at?: string;
  surveys_completed: number;
  created_at: string;
  is_admin?: boolean; // Admin access for analytics dashboard
}

/**
 * Magic link verify response
 */
export interface MagicLinkVerifyResponse {
  session_token: string;
  user: AuthenticatedUser;
  bronze_migration?: {
    bronze_username: string;
    sessions_migrated: number;
    scans_carried_over: number;
    books_scanned: number;
  };
}

/**
 * Auth code exchange request (Safari fix v6)
 */
export interface AuthCodeExchangeRequest {
  code: string;
}

/**
 * Auth code exchange response (Safari fix v6)
 */
export interface AuthCodeExchangeResponse {
  session_token: string;
  user: AuthenticatedUser;
}

/**
 * Current user response (Silver/Gold)
 */
export interface CurrentUserResponse {
  user: AuthenticatedUser;
  session_expires_at?: string;
}

/**
 * Stored auth state
 */
export interface AuthState {
  isAuthenticated: boolean;
  tier: AuthTier | null;
  username: string | null;
  email: string | null;
  deviceToken: string | null;
  sessionToken: string | null;
}

// =============================================================================
// Constants
// =============================================================================

const AUTH_BASE_URL = `${config.api.baseUrl}/api/v1/auth`;
const DEVICE_TOKEN_KEY = "sbp_device_token";
const SESSION_TOKEN_KEY = "sbp_session_token";
const AUTH_USER_KEY = "sbp_auth_user";

// =============================================================================
// Token Storage
// =============================================================================

/**
 * Get stored device token (Bronze tier)
 */
export function getDeviceToken(): string | null {
  try {
    return localStorage.getItem(DEVICE_TOKEN_KEY);
  } catch {
    return null;
  }
}

/**
 * Store device token (Bronze tier)
 */
export function setDeviceToken(token: string): void {
  try {
    localStorage.setItem(DEVICE_TOKEN_KEY, token);
  } catch {
    console.warn("Failed to store device token");
  }
}

/**
 * Clear device token
 */
export function clearDeviceToken(): void {
  try {
    localStorage.removeItem(DEVICE_TOKEN_KEY);
  } catch {
    // Ignore
  }
}

/**
 * Get stored session token (Silver/Gold tier)
 * @deprecated Session tokens are now stored in HttpOnly cookies.
 * This function is kept for backward compatibility with existing localStorage data.
 * Returns null as tokens are no longer accessible from JavaScript.
 */
export function getSessionToken(): string | null {
  // Session tokens are now in HttpOnly cookies (not accessible from JavaScript)
  // Check if old token exists in localStorage for migration purposes
  try {
    const oldToken = localStorage.getItem(SESSION_TOKEN_KEY);
    if (oldToken) {
      // Clean up old token
      localStorage.removeItem(SESSION_TOKEN_KEY);
      console.info("Migrated session token to HttpOnly cookie");
    }
  } catch {
    // Ignore
  }
  return null;
}

/**
 * Store session token (Silver/Gold tier)
 * @deprecated Session tokens are now stored in HttpOnly cookies by the server.
 * This function is kept for backward compatibility but does nothing.
 */
export function setSessionToken(_token: string): void {
  // Session tokens are now set via HttpOnly cookies by the server
  // No client-side storage needed
  console.debug("Session token set via HttpOnly cookie (server-side)");
}

/**
 * Clear session token
 * @deprecated Session tokens are now cleared server-side via cookie deletion.
 * This function is kept for backward compatibility.
 */
export function clearSessionToken(): void {
  // Session tokens are cleared server-side via cookie deletion
  // Clean up any legacy localStorage tokens
  try {
    localStorage.removeItem(SESSION_TOKEN_KEY);
  } catch {
    // Ignore
  }
}

/**
 * Get stored user info
 */
export function getStoredUser(): Partial<AuthenticatedUser> | null {
  try {
    const data = localStorage.getItem(AUTH_USER_KEY);
    return data ? JSON.parse(data) : null;
  } catch {
    return null;
  }
}

/**
 * Store user info
 * Returns true if storage succeeded, false otherwise
 */
export function setStoredUser(user: Partial<AuthenticatedUser>): boolean {
  try {
    const serialized = JSON.stringify(user);
    localStorage.setItem(AUTH_USER_KEY, serialized);

    // Verify the write actually persisted (Safari can silently fail)
    const verified = localStorage.getItem(AUTH_USER_KEY);
    if (verified !== serialized) {
      console.error(
        "localStorage write verification failed",
        "expected:",
        serialized.substring(0, 100),
        "got:",
        verified?.substring(0, 100),
      );
      Sentry.captureMessage("localStorage write verification failed", {
        level: "error",
        extra: {
          expected: serialized.substring(0, 100),
          actual: verified?.substring(0, 100),
          userTier: user.tier,
        },
      });
      return false;
    }
    return true;
  } catch (err) {
    console.warn("Failed to store user info:", err);
    Sentry.captureException(err, {
      tags: { action: "setStoredUser" },
      extra: { userTier: user.tier },
    });
    return false;
  }
}

/**
 * Clear stored user info
 */
export function clearStoredUser(): void {
  try {
    localStorage.removeItem(AUTH_USER_KEY);
  } catch {
    // Ignore
  }
}

/**
 * Clear all auth data
 */
export function clearAllAuthData(): void {
  clearDeviceToken();
  clearSessionToken();
  clearStoredUser();
}

/**
 * Get current auth state
 *
 * Note: Session tokens are now stored in HttpOnly cookies (not accessible from JS).
 * We determine Silver/Gold authentication based on the stored user's tier.
 * The actual session validity is verified server-side when API calls are made.
 */
export function getAuthState(): AuthState {
  const deviceToken = getDeviceToken();
  const user = getStoredUser();

  // Check for Silver/Gold tier (session-based auth via HttpOnly cookie)
  // We rely on stored user tier since we can't access the session token
  if (user && (user.tier === "silver" || user.tier === "gold")) {
    return {
      isAuthenticated: true,
      tier: user.tier as AuthTier,
      username: user.username || null,
      email: user.email || null,
      deviceToken: null,
      sessionToken: null, // Session token is in HttpOnly cookie, not accessible
    };
  }

  // Check for Bronze tier (device token-based auth)
  if (deviceToken && user && user.tier === "bronze") {
    return {
      isAuthenticated: true,
      tier: "bronze",
      username: user.username || null,
      email: null,
      deviceToken,
      sessionToken: null,
    };
  }

  return {
    isAuthenticated: false,
    tier: null,
    username: null,
    email: null,
    deviceToken: null,
    sessionToken: null,
  };
}

// =============================================================================
// API Helpers
// =============================================================================

/**
 * Create request headers
 *
 * Note: Authentication is now handled via HttpOnly cookies.
 * Authorization header is no longer sent (kept as parameter for backward compatibility).
 */
function createHeaders(_sessionToken?: string): HeadersInit {
  const headers: HeadersInit = {
    "Content-Type": "application/json",
    Accept: "application/json",
  };

  // Note: We no longer send Authorization header
  // Authentication is handled via HttpOnly cookies sent automatically by browser

  return headers;
}

/**
 * Parse error response
 */
async function parseError(response: Response): Promise<AuthError> {
  try {
    const data = await response.json();
    if (data.detail && typeof data.detail === "object") {
      return {
        code: data.detail.error || "UNKNOWN_ERROR",
        message: data.detail.message || "An error occurred",
      };
    }
    return {
      code: "UNKNOWN_ERROR",
      message: data.detail || data.message || "An error occurred",
    };
  } catch {
    return {
      code: "UNKNOWN_ERROR",
      message: response.statusText || "An error occurred",
    };
  }
}

/**
 * Generate device fingerprint for Bronze registration
 */
function generateDeviceFingerprint(): string {
  const canvas = document.createElement("canvas");
  const gl = canvas.getContext("webgl");
  const debugInfo = gl?.getExtension("WEBGL_debug_renderer_info");
  const renderer = debugInfo
    ? gl?.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL)
    : "unknown";

  const fingerprint = [
    navigator.userAgent,
    navigator.language,
    screen.width,
    screen.height,
    screen.colorDepth,
    new Date().getTimezoneOffset(),
    renderer,
  ].join("|");

  return fingerprint.substring(0, 500);
}

// =============================================================================
// Bronze Tier API
// =============================================================================

/**
 * Register a new Bronze tier user (device-based)
 */
export async function registerBronze(): Promise<BronzeRegisterResponse> {
  Sentry.addBreadcrumb({
    category: "auth",
    message: "Bronze registration start",
    level: "info",
  });

  const fingerprint = generateDeviceFingerprint();

  try {
    const response = await fetch(`${AUTH_BASE_URL}/register/bronze`, {
      method: "POST",
      headers: createHeaders(),
      credentials: "include", // Include cookies for session management
      body: JSON.stringify({ device_fingerprint: fingerprint }),
    });

    if (!response.ok) {
      const error = await parseError(response);
      Sentry.addBreadcrumb({
        category: "auth",
        message: "Bronze registration failed",
        level: "error",
        data: { error: error.message },
      });
      throw new Error(error.message);
    }

    const data: BronzeRegisterResponse = await response.json();

    // Store credentials
    setDeviceToken(data.device_token);
    setStoredUser({
      username: data.username,
      tier: data.tier,
    });

    // Backup to IndexedDB for mobile persistence
    backupDeviceTokenToIndexedDB(data.device_token).catch((err) => {
      console.warn("Failed to backup device token to IndexedDB:", err);
    });

    // Set user context for Sentry
    Sentry.setUser({ username: data.username });
    Sentry.setTag("user.tier", "bronze");

    Sentry.addBreadcrumb({
      category: "auth",
      message: "Bronze registration successful",
      level: "info",
      data: { tier: "bronze", username: data.username },
    });

    return data;
  } catch (error) {
    Sentry.captureException(error, {
      tags: { action: "registerBronze" },
    });
    throw error;
  }
}

/**
 * Login with existing Bronze device token
 */
export async function loginBronze(
  deviceToken: string,
): Promise<BronzeLoginResponse> {
  Sentry.addBreadcrumb({
    category: "auth",
    message: "Bronze login start",
    level: "info",
  });

  try {
    const response = await fetch(
      `${AUTH_BASE_URL}/login/bronze?device_token=${encodeURIComponent(deviceToken)}`,
      {
        method: "POST",
        headers: createHeaders(),
        credentials: "include", // Include cookies for session management
      },
    );

    if (!response.ok) {
      const error = await parseError(response);
      Sentry.addBreadcrumb({
        category: "auth",
        message: "Bronze login failed",
        level: "error",
        data: { error: error.message },
      });
      throw new Error(error.message);
    }

    const data: BronzeLoginResponse = await response.json();

    // Note: Session is now in HttpOnly cookie, not returned in response
    // Store device token for Bronze tier
    setDeviceToken(deviceToken);
    setStoredUser({
      username: data.username,
      tier: data.tier,
    });

    // Backup to IndexedDB for mobile persistence
    backupDeviceTokenToIndexedDB(deviceToken).catch((err) => {
      console.warn("Failed to backup device token to IndexedDB:", err);
    });

    // Set user context for Sentry
    Sentry.setUser({ username: data.username });
    Sentry.setTag("user.tier", "bronze");

    Sentry.addBreadcrumb({
      category: "auth",
      message: "Bronze login successful",
      level: "info",
      data: { tier: "bronze", username: data.username },
    });

    return data;
  } catch (error) {
    Sentry.captureException(error, {
      tags: { action: "loginBronze" },
    });
    throw error;
  }
}

/**
 * Get current Bronze user info
 */
export async function getBronzeUser(
  deviceToken: string,
): Promise<Record<string, unknown>> {
  const response = await fetch(
    `${AUTH_BASE_URL}/me/bronze?device_token=${encodeURIComponent(deviceToken)}`,
    {
      method: "GET",
      headers: createHeaders(),
      credentials: "include", // Include cookies for session management
    },
  );

  if (!response.ok) {
    const error = await parseError(response);
    throw new Error(error.message);
  }

  return response.json();
}

// =============================================================================
// Silver/Gold Tier API (Magic Link)
// =============================================================================

/**
 * Send magic link email for Silver/Gold authentication
 */
export async function sendMagicLink(
  email: string,
  deviceToken?: string,
): Promise<MagicLinkSendResponse> {
  Sentry.addBreadcrumb({
    category: "auth",
    message: "Magic link send start",
    level: "info",
  });

  const body: MagicLinkSendRequest = { email };
  if (deviceToken) {
    body.device_token = deviceToken;
  }

  try {
    const response = await fetch(`${AUTH_BASE_URL}/magic-link/send`, {
      method: "POST",
      headers: createHeaders(),
      credentials: "include", // Include cookies for session management
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      const error = await parseError(response);
      Sentry.addBreadcrumb({
        category: "auth",
        message: "Magic link send failed",
        level: "error",
        data: { error: error.message },
      });
      throw new Error(error.message);
    }

    Sentry.addBreadcrumb({
      category: "auth",
      message: "Magic link sent successfully",
      level: "info",
    });

    return response.json();
  } catch (error) {
    Sentry.captureException(error, {
      tags: { action: "sendMagicLink" },
    });
    throw error;
  }
}

/**
 * Verify magic link token and create session
 */
export async function verifyMagicLink(
  token: string,
): Promise<MagicLinkVerifyResponse> {
  const requestUrl = `${AUTH_BASE_URL}/magic-link/verify?token=${encodeURIComponent(token)}`;

  Sentry.addBreadcrumb({
    category: "auth",
    message: "Magic link verification start",
    level: "info",
    data: {
      url: requestUrl,
      authBaseUrl: AUTH_BASE_URL,
      tokenLength: token?.length,
    },
  });

  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000);

    Sentry.addBreadcrumb({
      category: "fetch",
      message: "Starting fetch request",
      level: "info",
      data: { url: requestUrl },
    });

    const response = await fetch(requestUrl, {
      method: "GET",
      headers: createHeaders(),
      credentials: "include",
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    Sentry.addBreadcrumb({
      category: "fetch",
      message: "Fetch response received",
      level: "info",
      data: {
        status: response.status,
        statusText: response.statusText,
        ok: response.ok,
        headers: {
          contentType: response.headers.get("content-type"),
          cors: response.headers.get("access-control-allow-origin"),
        },
      },
    });

    if (!response.ok) {
      const error = await parseError(response);
      Sentry.addBreadcrumb({
        category: "auth",
        message: "Magic link verification failed",
        level: "error",
        data: { error: error.message, status: response.status },
      });
      throw new Error(error.message);
    }

    const data: MagicLinkVerifyResponse = await response.json();

    // Note: Session token is now set in HttpOnly cookie by server
    // We no longer store it in localStorage (setSessionToken does nothing)
    // But we still call it for backward compatibility
    setSessionToken(data.session_token);

    // Store user data in localStorage with verification
    const storageSuccess = setStoredUser(data.user);
    if (!storageSuccess) {
      console.warn("localStorage write failed, relying on IndexedDB backup");
    }

    // Always backup to IndexedDB for Silver/Gold (more persistent on mobile Safari)
    // [Safari Fix] Made blocking (await) to ensure backup completes before redirect
    if (data.user.tier === "silver" || data.user.tier === "gold") {
      try {
        // Backup user data
        await backupUserDataToIndexedDB(data.user);
        console.log(
          `[Safari Fix] IndexedDB user backup completed for tier: ${data.user.tier}`,
        );

        // CRITICAL: Also backup session token to IndexedDB
        // This is a workaround for Safari ITP blocking HttpOnly cookies
        // when navigating from external apps (Mail → Safari)
        await backupSessionTokenToIndexedDB(data.session_token);
        console.log("[Safari Fix] IndexedDB session token backup completed");

        Sentry.addBreadcrumb({
          category: "auth.safari_fix",
          message: "IndexedDB backup completed (user + session token)",
          level: "info",
          data: { tier: data.user.tier, email: data.user.email },
        });
      } catch (err) {
        console.warn("[Safari Fix] IndexedDB backup failed:", err);
        Sentry.captureMessage("IndexedDB backup failed in verifyMagicLink", {
          level: "warning",
          extra: { error: err instanceof Error ? err.message : String(err) },
        });
      }

      // Clear device token when upgrading to Silver/Gold tier
      // This prevents fallback to Bronze tier if session cookie detection fails
      clearDeviceToken();
      // Also clear device token from IndexedDB backup
      clearDeviceTokenFromIndexedDB().catch((err) => {
        console.warn("Failed to clear device token from IndexedDB:", err);
      });
    }

    // [Safari Fix] Log storage state after verification for debugging
    const postVerifyStoredUser = getStoredUser();
    console.log("[Safari Fix] Post-verification storage state:", {
      hasStoredUser: !!postVerifyStoredUser,
      storedUserTier: postVerifyStoredUser?.tier,
      localStorageWriteSuccess: storageSuccess,
    });
    Sentry.addBreadcrumb({
      category: "auth.safari_fix",
      message: "Post-verification storage state",
      level: "info",
      data: {
        hasStoredUser: !!postVerifyStoredUser,
        storedUserTier: postVerifyStoredUser?.tier,
        localStorageWriteSuccess: storageSuccess,
      },
    });

    // Set user context for Sentry (Silver/Gold tier)
    Sentry.setUser({
      id: data.user.id?.toString(),
      email: data.user.email,
      username: data.user.username,
    });
    Sentry.setTag("user.tier", data.user.tier);

    Sentry.addBreadcrumb({
      category: "auth",
      message: "Magic link verification successful",
      level: "info",
      data: { tier: data.user.tier, username: data.user.username },
    });

    return data;
  } catch (error) {
    const err = error as Error;
    Sentry.addBreadcrumb({
      category: "fetch",
      message: "Fetch error caught",
      level: "error",
      data: {
        errorName: err.name,
        errorMessage: err.message,
        isAbortError: err.name === "AbortError",
        isTypeError: err.name === "TypeError",
        stack: err.stack?.substring(0, 500),
      },
    });

    Sentry.captureException(error, {
      tags: { action: "verifyMagicLink" },
      extra: {
        requestUrl,
        authBaseUrl: AUTH_BASE_URL,
        tokenProvided: !!token,
        tokenLength: token?.length,
        errorName: err.name,
        errorMessage: err.message,
      },
    });
    throw error;
  }
}

/**
 * Exchange authorization code for session token (Safari fix v6)
 *
 * This is the secure auth code exchange pattern. The magic link callback
 * returns a short-lived auth code (60s TTL) instead of the session token
 * directly in the URL. The frontend then exchanges this code for the
 * actual session token via POST request.
 *
 * Security benefits:
 * - Session tokens never appear in URLs, browser history, or server logs
 * - Auth codes are one-time use and expire quickly
 * - Exchange happens over POST with HttpOnly cookie response
 */
export async function exchangeAuthCode(
  code: string,
): Promise<AuthCodeExchangeResponse> {
  const requestUrl = `${AUTH_BASE_URL}/code/exchange`;

  Sentry.addBreadcrumb({
    category: "auth",
    message: "Auth code exchange start",
    level: "info",
    data: {
      url: requestUrl,
      codeLength: code?.length,
    },
  });

  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000);

    const response = await fetch(requestUrl, {
      method: "POST",
      headers: createHeaders(),
      credentials: "include",
      signal: controller.signal,
      body: JSON.stringify({ code }),
    });

    clearTimeout(timeoutId);

    Sentry.addBreadcrumb({
      category: "fetch",
      message: "Auth code exchange response received",
      level: "info",
      data: {
        status: response.status,
        statusText: response.statusText,
        ok: response.ok,
      },
    });

    if (!response.ok) {
      const error = await parseError(response);
      Sentry.addBreadcrumb({
        category: "auth",
        message: "Auth code exchange failed",
        level: "error",
        data: { error: error.message, status: response.status },
      });
      throw new Error(error.message);
    }

    const data: AuthCodeExchangeResponse = await response.json();

    // Store user data in localStorage
    const storageSuccess = setStoredUser(data.user);
    if (!storageSuccess) {
      console.warn(
        "[Safari Fix v6] localStorage write failed, relying on IndexedDB backup",
      );
    }

    // CRITICAL: Store session token to localStorage for Safari ITP fallback
    // The Authorization header in createHeaders() reads from this key
    // Without this, subsequent API calls won't include auth and default to Guest tier
    // See: docs/technical/SAFARI_SCAN_LIMIT_REGRESSION_FIX.md
    let sessionTokenStoredToLocalStorage = false;
    try {
      localStorage.setItem(SESSION_TOKEN_KEY, data.session_token);
      sessionTokenStoredToLocalStorage = true;

      Sentry.addBreadcrumb({
        category: "auth",
        message: "[Safari Fix v6] Session token stored to localStorage",
        level: "info",
        data: {
          tokenLength: data.session_token?.length,
          userTier: data.user.tier,
          key: SESSION_TOKEN_KEY,
        },
      });
    } catch (e) {
      console.warn(
        "[Safari Fix v6] Failed to store session token to localStorage:",
        e,
      );

      Sentry.addBreadcrumb({
        category: "auth",
        message:
          "[Safari Fix v6] FAILED to store session token to localStorage",
        level: "warning",
        data: {
          error: e instanceof Error ? e.message : String(e),
          userTier: data.user.tier,
        },
      });

      Sentry.captureMessage(
        "Safari Fix v6: Failed to store session token to localStorage",
        {
          level: "warning",
          extra: {
            error: e instanceof Error ? e.message : String(e),
            userTier: data.user.tier,
            tokenProvided: !!data.session_token,
          },
        },
      );
    }

    // Backup to IndexedDB for Safari persistence
    if (data.user.tier === "silver" || data.user.tier === "gold") {
      try {
        await backupUserDataToIndexedDB(data.user);
        await backupSessionTokenToIndexedDB(data.session_token);
        console.log(
          `[Safari Fix v6] IndexedDB backup completed for tier: ${data.user.tier}`,
        );
      } catch (err) {
        console.warn("[Safari Fix v6] IndexedDB backup failed:", err);
        Sentry.captureMessage("IndexedDB backup failed in exchangeAuthCode", {
          level: "warning",
          extra: { error: err instanceof Error ? err.message : String(err) },
        });
      }

      // Clear device token when upgrading to Silver/Gold tier
      clearDeviceToken();
      clearDeviceTokenFromIndexedDB().catch((err) => {
        console.warn("Failed to clear device token from IndexedDB:", err);
      });
    }

    // Set Sentry user context
    Sentry.setUser({
      id: data.user.id?.toString(),
      email: data.user.email,
      username: data.user.username,
    });
    Sentry.setTag("user.tier", data.user.tier);

    Sentry.addBreadcrumb({
      category: "auth",
      message: "Auth code exchange successful",
      level: "info",
      data: {
        tier: data.user.tier,
        username: data.user.username,
        // Track localStorage status for Safari scan limit regression debugging
        sessionTokenInLocalStorage: sessionTokenStoredToLocalStorage,
      },
    });

    return data;
  } catch (error) {
    const err = error as Error;
    Sentry.captureException(error, {
      tags: { action: "exchangeAuthCode" },
      extra: {
        requestUrl,
        codeProvided: !!code,
        codeLength: code?.length,
        errorName: err.name,
        errorMessage: err.message,
      },
    });
    throw error;
  }
}

/**
 * Get current authenticated user info (Silver/Gold)
 */
export async function getCurrentUser(): Promise<CurrentUserResponse> {
  // Note: Session token is now in HttpOnly cookie
  // No need to check localStorage for session token
  const response = await fetch(`${AUTH_BASE_URL}/me`, {
    method: "GET",
    headers: createHeaders(),
    credentials: "include", // Send HttpOnly cookie with request
  });

  if (!response.ok) {
    const error = await parseError(response);
    // IMPORTANT: Do NOT clear session token or stored user on 401!
    //
    // Why? This function uses HttpOnly cookies for auth. If Safari ITP blocks
    // the cookie, we get 401 even though the localStorage session token is
    // still valid. Other API calls (in api.ts) use Authorization header as
    // fallback, which reads from localStorage. Clearing here would break that.
    //
    // Callers that need to handle 401 (like tryRecoverUserSession) already
    // save/restore auth state. Explicit logout() is the proper way to clear.
    //
    // See: Safari ITP session persistence fix (January 2026)
    if (response.status === 401) {
      Sentry.addBreadcrumb({
        category: "auth",
        message: "getCurrentUser() returned 401 - NOT clearing localStorage",
        level: "warning",
        data: {
          reason:
            "Safari ITP may have blocked cookie; localStorage token may still be valid",
        },
      });
      console.debug(
        "[Auth] getCurrentUser() 401 - preserving localStorage (Safari ITP resilience)",
      );
    }
    throw new Error(error.message);
  }

  const data: CurrentUserResponse = await response.json();

  // Update stored user
  setStoredUser(data.user);

  return data;
}

/**
 * Logout current user
 */
export async function logout(): Promise<void> {
  Sentry.addBreadcrumb({
    category: "auth",
    message: "Logout initiated",
    level: "info",
  });

  // Note: Session token is now in HttpOnly cookie
  // Call logout endpoint to clear server-side cookie
  try {
    await fetch(`${AUTH_BASE_URL}/logout`, {
      method: "POST",
      headers: createHeaders(),
      credentials: "include", // Send HttpOnly cookie to identify session
    });
  } catch {
    // Ignore errors on logout
    console.warn("Logout request failed, clearing local state anyway");
  }

  // Clear all local auth data
  // Note: HttpOnly cookie is cleared server-side
  clearAllAuthData();

  // Clear IndexedDB backups to prevent session recovery after explicit logout
  // This is async but we don't await - best effort cleanup
  clearDeviceTokenFromIndexedDB().catch((err) => {
    console.warn("Failed to clear device token from IndexedDB on logout:", err);
  });
  clearUserDataFromIndexedDB().catch((err) => {
    console.warn("Failed to clear user data from IndexedDB on logout:", err);
  });
  clearSessionTokenFromIndexedDB().catch((err) => {
    console.warn(
      "Failed to clear session token from IndexedDB on logout:",
      err,
    );
  });

  // Clear user context from Sentry
  Sentry.setUser(null);

  Sentry.addBreadcrumb({
    category: "auth",
    message: "Logout successful",
    level: "info",
  });
}

// =============================================================================
// Auth State Helpers
// =============================================================================

/**
 * Check if user is authenticated (any tier)
 */
export function isAuthenticated(): boolean {
  return getAuthState().isAuthenticated;
}

/**
 * Check if user has Bronze tier
 */
export function isBronzeTier(): boolean {
  const state = getAuthState();
  return state.isAuthenticated && state.tier === "bronze";
}

/**
 * Check if user has Silver or higher tier
 */
export function isSilverOrHigher(): boolean {
  const state = getAuthState();
  return (
    state.isAuthenticated && (state.tier === "silver" || state.tier === "gold")
  );
}

/**
 * Check if user has Gold tier
 */
export function isGoldTier(): boolean {
  const state = getAuthState();
  return state.isAuthenticated && state.tier === "gold";
}

/**
 * Format user display name for UI components
 *
 * Returns a shortened display name based on user tier:
 * - Guest: "Login"
 * - Bronze: Full username (e.g., "user8u37J")
 * - Silver/Gold: Username if set, otherwise truncated email prefix (max 10 chars)
 */
export function formatUserDisplay(
  authState?: AuthState,
  storedUser?: Partial<AuthenticatedUser> | null,
): string {
  const state = authState || getAuthState();
  const user = storedUser === undefined ? getStoredUser() : storedUser;

  if (!state.isAuthenticated) {
    return "Login";
  }

  // For Bronze tier: show full username (e.g., "user8u37J")
  if (state.tier === "bronze") {
    return state.username || user?.username || "User";
  }

  // For Silver/Gold: prefer username if set
  if (user?.username) {
    return user.username;
  }

  // Fallback: truncated email prefix (max 10 chars)
  const email = state.email || user?.email;
  if (email) {
    const emailPrefix = email.split("@")[0];
    return emailPrefix.length > 10 ? emailPrefix.substring(0, 10) : emailPrefix;
  }

  return "User";
}
// =============================================================================
// Device Memory & Recovery
// =============================================================================

/**
 * Check device response from API
 */
export interface CheckDeviceResponse {
  found: boolean;
  device_token?: string;
  username?: string;
  tier?: string;
  scan_limit?: number;
}

/**
 * IndexedDB storage key for device token backup
 */
const DEVICE_TOKEN_IDB_KEY = "sbp_device_token_backup";
const IDB_DB_NAME = "sbp_device_storage";
const IDB_STORE_NAME = "device_tokens";

/**
 * Save device token to IndexedDB as backup storage
 * IndexedDB is more persistent than localStorage on mobile Safari
 */
export async function backupDeviceTokenToIndexedDB(
  deviceToken: string,
): Promise<void> {
  Sentry.addBreadcrumb({
    category: "auth.device_memory",
    message: "IndexedDB backup initiated",
    level: "debug",
  });

  try {
    const db = await openDeviceDatabase();
    const tx = db.transaction(IDB_STORE_NAME, "readwrite");
    const store = tx.objectStore(IDB_STORE_NAME);
    await new Promise<void>((resolve, reject) => {
      const request = store.put({
        key: DEVICE_TOKEN_IDB_KEY,
        value: deviceToken,
        timestamp: Date.now(),
      });
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
    db.close();

    Sentry.addBreadcrumb({
      category: "auth.device_memory",
      message: "Device token backed up to IndexedDB successfully",
      level: "info",
    });
    console.debug("Device token backed up to IndexedDB");
  } catch (error) {
    Sentry.addBreadcrumb({
      category: "auth.device_memory",
      message: "IndexedDB backup failed",
      level: "warning",
      data: {
        error: error instanceof Error ? error.message : String(error),
      },
    });
    console.warn("Failed to backup device token to IndexedDB:", error);
  }
}

/**
 * Retrieve device token from IndexedDB backup
 */
export async function getDeviceTokenFromIndexedDB(): Promise<string | null> {
  Sentry.addBreadcrumb({
    category: "auth.device_memory",
    message: "IndexedDB retrieval initiated",
    level: "debug",
  });

  try {
    const db = await openDeviceDatabase();
    const tx = db.transaction(IDB_STORE_NAME, "readonly");
    const store = tx.objectStore(IDB_STORE_NAME);
    const result = await new Promise<{ value: string } | undefined>(
      (resolve, reject) => {
        const request = store.get(DEVICE_TOKEN_IDB_KEY);
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
      },
    );
    db.close();

    const found = result?.value ?? null;
    Sentry.addBreadcrumb({
      category: "auth.device_memory",
      message: found
        ? "Device token found in IndexedDB"
        : "No device token in IndexedDB",
      level: found ? "info" : "debug",
    });

    return found;
  } catch (error) {
    Sentry.addBreadcrumb({
      category: "auth.device_memory",
      message: "IndexedDB retrieval failed",
      level: "warning",
      data: {
        error: error instanceof Error ? error.message : String(error),
      },
    });
    console.warn("Failed to get device token from IndexedDB:", error);
    return null;
  }
}

/**
 * Clear device token from IndexedDB backup
 * Called during logout to prevent session recovery after explicit logout
 */
export async function clearDeviceTokenFromIndexedDB(): Promise<void> {
  Sentry.addBreadcrumb({
    category: "auth.device_memory",
    message: "IndexedDB clear initiated",
    level: "debug",
  });

  try {
    const db = await openDeviceDatabase();
    const tx = db.transaction(IDB_STORE_NAME, "readwrite");
    const store = tx.objectStore(IDB_STORE_NAME);
    await new Promise<void>((resolve, reject) => {
      const request = store.delete(DEVICE_TOKEN_IDB_KEY);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
    db.close();

    Sentry.addBreadcrumb({
      category: "auth.device_memory",
      message: "Device token cleared from IndexedDB",
      level: "info",
    });
    console.debug("Device token cleared from IndexedDB");
  } catch (error) {
    Sentry.addBreadcrumb({
      category: "auth.device_memory",
      message: "IndexedDB clear failed",
      level: "warning",
      data: {
        error: error instanceof Error ? error.message : String(error),
      },
    });
    console.warn("Failed to clear device token from IndexedDB:", error);
  }
}

// =============================================================================
// IndexedDB User Data Backup (Silver/Gold)
// =============================================================================

const USER_DATA_IDB_KEY = "sbp_user_data_backup";

/**
 * Save user data to IndexedDB as backup storage
 * IndexedDB is more persistent than localStorage on mobile Safari
 */
export async function backupUserDataToIndexedDB(
  user: Partial<AuthenticatedUser>,
): Promise<void> {
  Sentry.addBreadcrumb({
    category: "auth.user_backup",
    message: "User data IndexedDB backup initiated",
    level: "debug",
    data: { tier: user.tier },
  });

  try {
    const db = await openDeviceDatabase();
    const tx = db.transaction(IDB_STORE_NAME, "readwrite");
    const store = tx.objectStore(IDB_STORE_NAME);
    await new Promise<void>((resolve, reject) => {
      const request = store.put({
        key: USER_DATA_IDB_KEY,
        value: JSON.stringify(user),
        timestamp: Date.now(),
      });
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
    db.close();

    Sentry.addBreadcrumb({
      category: "auth.user_backup",
      message: "User data backed up to IndexedDB successfully",
      level: "info",
      data: { tier: user.tier },
    });
    console.debug("User data backed up to IndexedDB");
  } catch (error) {
    Sentry.addBreadcrumb({
      category: "auth.user_backup",
      message: "User data IndexedDB backup failed",
      level: "warning",
      data: {
        error: error instanceof Error ? error.message : String(error),
      },
    });
    console.warn("Failed to backup user data to IndexedDB:", error);
  }
}

/**
 * Retrieve user data from IndexedDB backup
 */
export async function getUserDataFromIndexedDB(): Promise<Partial<AuthenticatedUser> | null> {
  Sentry.addBreadcrumb({
    category: "auth.user_backup",
    message: "User data IndexedDB retrieval initiated",
    level: "debug",
  });

  try {
    const db = await openDeviceDatabase();
    const tx = db.transaction(IDB_STORE_NAME, "readonly");
    const store = tx.objectStore(IDB_STORE_NAME);
    const result = await new Promise<{ value: string } | undefined>(
      (resolve, reject) => {
        const request = store.get(USER_DATA_IDB_KEY);
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
      },
    );
    db.close();

    if (result?.value) {
      const user = JSON.parse(result.value) as Partial<AuthenticatedUser>;
      Sentry.addBreadcrumb({
        category: "auth.user_backup",
        message: "User data found in IndexedDB",
        level: "info",
        data: { tier: user.tier },
      });
      return user;
    }

    Sentry.addBreadcrumb({
      category: "auth.user_backup",
      message: "No user data in IndexedDB",
      level: "debug",
    });
    return null;
  } catch (error) {
    Sentry.addBreadcrumb({
      category: "auth.user_backup",
      message: "User data IndexedDB retrieval failed",
      level: "warning",
      data: {
        error: error instanceof Error ? error.message : String(error),
      },
    });
    console.warn("Failed to get user data from IndexedDB:", error);
    return null;
  }
}

/**
 * Clear user data from IndexedDB backup
 * Called during logout to prevent session recovery after explicit logout
 */
export async function clearUserDataFromIndexedDB(): Promise<void> {
  try {
    const db = await openDeviceDatabase();
    const tx = db.transaction(IDB_STORE_NAME, "readwrite");
    const store = tx.objectStore(IDB_STORE_NAME);
    await new Promise<void>((resolve, reject) => {
      const request = store.delete(USER_DATA_IDB_KEY);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
    db.close();

    Sentry.addBreadcrumb({
      category: "auth.user_backup",
      message: "User data cleared from IndexedDB",
      level: "info",
    });
    console.debug("User data cleared from IndexedDB");
  } catch (error) {
    Sentry.addBreadcrumb({
      category: "auth.user_backup",
      message: "User data IndexedDB clear failed",
      level: "warning",
      data: {
        error: error instanceof Error ? error.message : String(error),
      },
    });
    console.warn("Failed to clear user data from IndexedDB:", error);
  }
}

// =============================================================================
// IndexedDB Session Token Backup (Safari ITP Workaround)
// =============================================================================

const SESSION_TOKEN_IDB_KEY = "sbp_session_token_backup";

/**
 * Save session token to IndexedDB as backup
 * This is a workaround for Safari ITP blocking HttpOnly cookies
 * when navigating from external apps (Mail → Safari)
 */
export async function backupSessionTokenToIndexedDB(
  sessionToken: string,
): Promise<void> {
  try {
    const db = await openDeviceDatabase();
    const tx = db.transaction(IDB_STORE_NAME, "readwrite");
    const store = tx.objectStore(IDB_STORE_NAME);
    await new Promise<void>((resolve, reject) => {
      const request = store.put({
        key: SESSION_TOKEN_IDB_KEY,
        value: sessionToken,
        timestamp: Date.now(),
      });
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
    db.close();

    console.log("[Safari Fix] Session token backed up to IndexedDB");
    Sentry.addBreadcrumb({
      category: "auth.safari_fix",
      message: "Session token backed up to IndexedDB",
      level: "info",
    });
  } catch (error) {
    console.warn("Failed to backup session token to IndexedDB:", error);
  }
}

/**
 * Retrieve session token from IndexedDB backup
 */
export async function getSessionTokenFromIndexedDB(): Promise<string | null> {
  try {
    const db = await openDeviceDatabase();
    const tx = db.transaction(IDB_STORE_NAME, "readonly");
    const store = tx.objectStore(IDB_STORE_NAME);
    const result = await new Promise<{ value: string } | undefined>(
      (resolve, reject) => {
        const request = store.get(SESSION_TOKEN_IDB_KEY);
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
      },
    );
    db.close();

    if (result?.value) {
      console.log("[Safari Fix] Session token found in IndexedDB");
      return result.value;
    }
    return null;
  } catch (error) {
    console.warn("Failed to get session token from IndexedDB:", error);
    return null;
  }
}

/**
 * Clear session token from IndexedDB backup
 */
export async function clearSessionTokenFromIndexedDB(): Promise<void> {
  try {
    const db = await openDeviceDatabase();
    const tx = db.transaction(IDB_STORE_NAME, "readwrite");
    const store = tx.objectStore(IDB_STORE_NAME);
    await new Promise<void>((resolve, reject) => {
      const request = store.delete(SESSION_TOKEN_IDB_KEY);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
    db.close();
    console.debug("Session token cleared from IndexedDB");
  } catch (error) {
    console.warn("Failed to clear session token from IndexedDB:", error);
  }
}

/**
 * Open IndexedDB database for device token storage
 */
function openDeviceDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(IDB_DB_NAME, 1);

    request.onerror = () => {
      Sentry.addBreadcrumb({
        category: "auth.device_memory",
        message: "IndexedDB open failed",
        level: "error",
        data: {
          error: request.error?.message || "Unknown error",
        },
      });
      reject(request.error);
    };

    request.onsuccess = () => resolve(request.result);

    request.onupgradeneeded = (event) => {
      Sentry.addBreadcrumb({
        category: "auth.device_memory",
        message: "IndexedDB upgrade needed - creating store",
        level: "info",
      });
      const db = (event.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(IDB_STORE_NAME)) {
        db.createObjectStore(IDB_STORE_NAME, { keyPath: "key" });
      }
    };
  });
}

/**
 * Check if this device has a registered Bronze account
 * Uses device fingerprint to look up existing registration
 */
export async function checkDevice(): Promise<CheckDeviceResponse> {
  Sentry.addBreadcrumb({
    category: "auth.device_memory",
    message: "Server-side device check initiated",
    level: "info",
  });

  const fingerprint = generateDeviceFingerprint();

  try {
    const response = await fetch(`${AUTH_BASE_URL}/check-device`, {
      method: "POST",
      headers: createHeaders(),
      credentials: "include",
      body: JSON.stringify({ device_fingerprint: fingerprint }),
    });

    if (!response.ok) {
      const error = await parseError(response);
      Sentry.addBreadcrumb({
        category: "auth.device_memory",
        message: "Device check API failed",
        level: "warning",
        data: {
          status: response.status,
          error: error.message,
        },
      });
      console.warn("Device check failed:", error.message);
      return { found: false };
    }

    const data: CheckDeviceResponse = await response.json();

    if (data.found && data.device_token) {
      // Restore credentials from device check
      setDeviceToken(data.device_token);
      setStoredUser({
        username: data.username,
        tier: data.tier,
      });

      // Also backup to IndexedDB for future recovery
      await backupDeviceTokenToIndexedDB(data.device_token);

      Sentry.addBreadcrumb({
        category: "auth.device_memory",
        message: "Device recovered via server fingerprint match",
        level: "info",
        data: { tier: data.tier, username: data.username },
      });

      // Set Sentry user context
      if (data.username) {
        Sentry.setUser({ username: data.username });
        Sentry.setTag("user.tier", data.tier || "bronze");
        Sentry.setTag("auth.recovery_method", "fingerprint");
      }
    } else {
      Sentry.addBreadcrumb({
        category: "auth.device_memory",
        message: "No matching device found on server",
        level: "debug",
      });
    }

    return data;
  } catch (error) {
    Sentry.addBreadcrumb({
      category: "auth.device_memory",
      message: "Device check network error",
      level: "error",
      data: {
        error: error instanceof Error ? error.message : String(error),
      },
    });
    console.warn("Device check error:", error);
    Sentry.captureException(error, {
      tags: { action: "checkDevice", feature: "device_memory" },
    });
    return { found: false };
  }
}

/**
 * Try to recover Bronze tier session on app initialization
 *
 * Recovery order:
 * 1. Check localStorage (fastest)
 * 2. Check IndexedDB backup (more persistent on mobile)
 * 3. Check server by device fingerprint (most reliable)
 *
 * @returns True if session was recovered, false otherwise
 */
export async function tryRecoverDeviceSession(): Promise<boolean> {
  const startTime = performance.now();

  // 1. Already have auth state in localStorage? No recovery needed
  const existingState = getAuthState();
  if (existingState.isAuthenticated) {
    Sentry.addBreadcrumb({
      category: "auth.device_memory",
      message: "Session exists in localStorage, no recovery needed",
      level: "debug",
      data: { tier: existingState.tier },
    });
    console.debug("Session already exists, no recovery needed");
    return true;
  }

  Sentry.addBreadcrumb({
    category: "auth.device_memory",
    message: "Device session recovery started",
    level: "info",
    data: { reason: "No auth state in localStorage" },
  });

  // 2. Try IndexedDB backup first (more persistent than localStorage)
  const backupToken = await getDeviceTokenFromIndexedDB();
  if (backupToken) {
    console.debug("Found device token in IndexedDB backup");
    try {
      // Verify token is still valid by logging in
      const loginResult = await loginBronze(backupToken);
      if (loginResult) {
        // Restore to localStorage
        setDeviceToken(backupToken);
        setStoredUser({
          username: loginResult.username,
          tier: loginResult.tier,
        });

        const duration = Math.round(performance.now() - startTime);
        Sentry.addBreadcrumb({
          category: "auth.device_memory",
          message: "Session recovered from IndexedDB backup",
          level: "info",
          data: {
            username: loginResult.username,
            tier: loginResult.tier,
            duration_ms: duration,
          },
        });
        Sentry.setTag("auth.recovery_method", "indexeddb");

        return true;
      }
    } catch (error) {
      Sentry.addBreadcrumb({
        category: "auth.device_memory",
        message: "IndexedDB token validation failed",
        level: "warning",
        data: {
          error: error instanceof Error ? error.message : String(error),
        },
      });
      console.warn("IndexedDB token invalid, trying fingerprint check:", error);
    }
  }

  // 3. Try server-side device fingerprint check (most reliable)
  const deviceCheckResult = await checkDevice();
  if (deviceCheckResult.found) {
    const duration = Math.round(performance.now() - startTime);
    Sentry.addBreadcrumb({
      category: "auth.device_memory",
      message: "Session recovered via server device fingerprint",
      level: "info",
      data: {
        username: deviceCheckResult.username,
        tier: deviceCheckResult.tier,
        duration_ms: duration,
      },
    });
    return true;
  }

  const duration = Math.round(performance.now() - startTime);
  Sentry.addBreadcrumb({
    category: "auth.device_memory",
    message: "Device session recovery failed - no matching device",
    level: "info",
    data: {
      checked_indexeddb: !!backupToken,
      checked_server: true,
      duration_ms: duration,
    },
  });
  console.debug("No existing device session found");
  return false;
}

/**
 * Try to recover Silver/Gold user session from IndexedDB
 *
 * Called when localStorage appears empty but user might have valid session.
 * This handles Safari's aggressive localStorage clearing on mobile.
 *
 * @returns Recovered user data if found, null otherwise
 */
export async function tryRecoverUserSession(): Promise<Partial<AuthenticatedUser> | null> {
  // 1. Already have user in localStorage? No recovery needed
  const existingUser = getStoredUser();
  if (
    existingUser &&
    (existingUser.tier === "silver" || existingUser.tier === "gold")
  ) {
    console.debug("User session exists in localStorage, no recovery needed");
    return existingUser;
  }

  Sentry.addBreadcrumb({
    category: "auth.user_backup",
    message: "User session recovery started",
    level: "info",
    data: { reason: "No Silver/Gold user in localStorage" },
  });

  // Save existing auth state before recovery attempts
  // This is a safety net in case any called function clears auth unexpectedly
  // (e.g., network errors, unexpected 401s from other endpoints)
  const savedDeviceToken = getDeviceToken();
  const savedStoredUser = existingUser; // Already captured above

  // 2. Try IndexedDB backup
  const backupUser = await getUserDataFromIndexedDB();
  if (
    backupUser &&
    (backupUser.tier === "silver" || backupUser.tier === "gold")
  ) {
    console.log(
      "[Safari Fix] Found user data in IndexedDB backup:",
      backupUser.tier,
    );

    // Try to restore to localStorage (may fail on Safari)
    const success = setStoredUser(backupUser);
    if (success) {
      Sentry.addBreadcrumb({
        category: "auth.user_backup",
        message:
          "User session recovered from IndexedDB and restored to localStorage",
        level: "info",
        data: { tier: backupUser.tier },
      });
    } else {
      // localStorage write failed - but we still have the data!
      console.warn(
        "[Safari Fix] localStorage write failed, but IndexedDB data is valid",
      );
      Sentry.addBreadcrumb({
        category: "auth.user_backup",
        message:
          "User session recovered from IndexedDB (localStorage write failed)",
        level: "warning",
        data: { tier: backupUser.tier },
      });
    }

    // Return the user data regardless of localStorage success
    Sentry.setTag("auth.recovery_method", "indexeddb_user");
    return backupUser;
  }

  console.log(
    "[Safari Fix] User data not in IndexedDB, trying session token...",
  );

  // 3. Try using session token from IndexedDB with Authorization header
  // This is a workaround for Safari ITP blocking HttpOnly cookies
  const sessionToken = await getSessionTokenFromIndexedDB();
  if (sessionToken) {
    console.log(
      "[Safari Fix] Found session token in IndexedDB, calling /auth/me with Authorization header",
    );
    try {
      Sentry.addBreadcrumb({
        category: "auth.safari_fix",
        message: "Attempting server recovery with IndexedDB session token",
        level: "info",
      });

      // Call /auth/me with Authorization header instead of cookie
      const response = await fetch(`${AUTH_BASE_URL}/me`, {
        method: "GET",
        headers: {
          ...createHeaders(),
          Authorization: `Bearer ${sessionToken}`,
        },
        credentials: "include",
      });

      if (response.ok) {
        const data: CurrentUserResponse = await response.json();
        if (
          data.user &&
          (data.user.tier === "silver" || data.user.tier === "gold")
        ) {
          console.log(
            "[Safari Fix] Recovered user from server via session token:",
            data.user.tier,
          );

          // Update localStorage if possible
          setStoredUser(data.user);

          // Re-backup user data to IndexedDB
          backupUserDataToIndexedDB(data.user).catch((err) => {
            console.warn("Failed to backup recovered user to IndexedDB:", err);
          });

          Sentry.addBreadcrumb({
            category: "auth.safari_fix",
            message:
              "User session recovered from server via IndexedDB session token",
            level: "info",
            data: { tier: data.user.tier },
          });
          Sentry.setTag("auth.recovery_method", "indexeddb_session_token");
          return data.user;
        }
      } else {
        console.log(
          "[Safari Fix] Server returned",
          response.status,
          "for session token",
        );
        // Token might be invalid/expired, clear it
        if (response.status === 401) {
          clearSessionTokenFromIndexedDB().catch(() => {});
        }
      }
    } catch (err) {
      console.log("[Safari Fix] Server call with session token failed:", err);
      Sentry.addBreadcrumb({
        category: "auth.safari_fix",
        message: "Server recovery with session token failed",
        level: "warning",
        data: { error: err instanceof Error ? err.message : String(err) },
      });
    }
  }

  // 4. Last resort: Try with HttpOnly cookie (might work in some cases)
  try {
    Sentry.addBreadcrumb({
      category: "auth.user_backup",
      message: "Attempting server-side session recovery via cookie",
      level: "info",
    });

    const response = await getCurrentUser();
    if (
      response.user &&
      (response.user.tier === "silver" || response.user.tier === "gold")
    ) {
      console.log(
        "[Safari Fix] Recovered user from server via cookie:",
        response.user.tier,
      );

      // Try to backup to IndexedDB for next time
      backupUserDataToIndexedDB(response.user).catch((err) => {
        console.warn("Failed to backup recovered user to IndexedDB:", err);
      });

      Sentry.addBreadcrumb({
        category: "auth.user_backup",
        message: "User session recovered from server via session cookie",
        level: "info",
        data: { tier: response.user.tier },
      });
      Sentry.setTag("auth.recovery_method", "server_cookie");
      return response.user;
    }
  } catch (err) {
    // Server recovery failed - probably no valid session cookie
    console.log("[Safari Fix] Cookie-based server recovery failed:", err);
    Sentry.addBreadcrumb({
      category: "auth.user_backup",
      message: "Server-side session recovery failed",
      level: "warning",
      data: { error: err instanceof Error ? err.message : String(err) },
    });

    // Restore any auth state that existed before recovery attempt
    // This is a safety net to preserve existing auth if recovery fails
    if (savedDeviceToken) {
      setDeviceToken(savedDeviceToken);
    }
    if (savedStoredUser) {
      setStoredUser(savedStoredUser);
      Sentry.addBreadcrumb({
        category: "auth.user_backup",
        message: "Restored existing auth state after failed recovery",
        level: "info",
        data: { tier: savedStoredUser.tier },
      });
    }
  }

  console.debug("No Silver/Gold user session found");
  return null;
}

// =============================================================================
// Session Tier Resolution
// =============================================================================

/**
 * User tier including guest (unauthenticated) state.
 * Extends AuthTier to include "guest" for session management.
 */
export type UserTier = "guest" | AuthTier;

/**
 * Determines if we should update the session tier from an API response.
 *
 * CRITICAL FIX: Prevents "logout on navigation" bug where API responses
 * returning "guest" would overwrite an authenticated user's tier.
 *
 * The auth state (localStorage/IndexedDB) is the source of truth for tier.
 * API responses should only UPGRADE tier, never DOWNGRADE from authenticated to guest.
 *
 * This can happen due to:
 * - Safari ITP blocking auth cookies
 * - Race conditions during auth initialization
 * - Timing issues with session cookie propagation
 *
 * @param currentTier - The current session tier
 * @param apiTier - The tier returned from the API
 * @returns The tier that should be used
 *
 * @example
 * ```typescript
 * // In a component updating from API response:
 * setSession(prev => ({
 *   ...prev,
 *   tier: resolveSessionTier(prev.tier, response.userLimits?.tier),
 * }));
 * ```
 */
export function resolveSessionTier(
  currentTier: UserTier,
  apiTier: UserTier | undefined,
): UserTier {
  // If API didn't return a tier, keep current
  if (!apiTier) {
    return currentTier;
  }

  // If current user is authenticated (not guest), don't downgrade to guest
  // This prevents the "logout on navigation" bug where Safari ITP or timing
  // issues cause the API to not recognize the authenticated session
  if (currentTier !== "guest" && apiTier === "guest") {
    console.debug(
      `[Auth] Preserving tier ${currentTier}, ignoring API guest response`,
    );
    return currentTier;
  }

  // Otherwise, use the API tier (allows upgrades and same-tier updates)
  return apiTier;
}
